/**
 * Clever Single Sign-On (District SSO, OAuth 2) — frontend helpers.
 *
 * The flow (see docs/clever-sso.md):
 *   LIWC button / Clever Portal → https://clever.com/oauth/authorize →
 *   /auth/clever/callback?code=&state= → cleverSsoExchange callable →
 *   Firebase custom token → the normal claims-ready gate in App.js.
 *
 * CSRF: button-initiated logins carry a per-attempt random `state` stored in
 * sessionStorage (one tab, one attempt). Portal-initiated logins arrive from
 * Clever WITHOUT state — per Clever's OAuth guidance the callback abandons
 * that code and restarts the flow from /auth/clever so it carries our state.
 *
 * Availability is keyed off REACT_APP_CLEVER_CLIENT_ID at build time: only the
 * dev pipeline injects it (GitHub secret DEV_REACT_APP_CLEVER_CLIENT_ID), so
 * prod and demo builds render no Clever UI. Read at call time, not module
 * load, so tests can toggle it.
 */

export const CLEVER_AUTHORIZE_URL = 'https://clever.com/oauth/authorize';

// sessionStorage keys (per-tab, cleared when the callback consumes them).
export const CLEVER_STATE_KEY = 'cleverSsoState';
export const CLEVER_RESTART_KEY = 'cleverSsoRestarted';

export function getCleverClientId() {
  return process.env.REACT_APP_CLEVER_CLIENT_ID || '';
}

export function isCleverSsoConfigured() {
  return getCleverClientId().length > 0;
}

// Must exactly match a redirect URI registered in the Clever dashboard
// (dev hosting URL + localhost for `npm start`).
export function cleverRedirectUri() {
  return `${window.location.origin}/auth/clever/callback`;
}

export function generateState() {
  const bytes = new Uint8Array(16);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function buildAuthorizeUrl(state) {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: getCleverClientId(),
    redirect_uri: cleverRedirectUri(),
    state,
  });
  return `${CLEVER_AUTHORIZE_URL}?${params.toString()}`;
}
